import {
  Grid,
  Menu,
  Popover,
  Separator,
  useToastAlert,
} from '@design-systems/canvas-ui'
import { useResponsiveLayout } from '@plm-core/utils'
import { useAuth } from '@praxis/component-auth'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import UserAvatar from 'users/components/UserAvatar'
import { UserContext } from './UserContext'
import ImpersonationModal from './impersonation/ImpersonationModal'
import {
  applyOriginalUser,
  restoreOriginalUser,
} from './impersonation/impersonationApi'
import { isPol } from './POLAccountSwitcher'
import {
  canShowImpersonationAction,
  IS_IMPERSONATING_KEY,
} from './impersonation/impersonationStorage'

type ProfileWrapperProps = {
  children: React.ReactNode
  displayName: string
  isLargeScreen: boolean
  companyName?: string
}

const ProfileWrapper = ({
  children,
  displayName,
  isLargeScreen,
  companyName,
}: ProfileWrapperProps) =>
  isLargeScreen ? (
    <>{children}</>
  ) : (
    <Popover
      size="sm"
      content={
        <Grid.Container direction="column" spacing="dense">
          <Grid.Item>
            <strong>{displayName}</strong>
          </Grid.Item>
          <Grid.Item>{companyName}</Grid.Item>
        </Grid.Container>
      }
      location="bottom-right"
    >
      {children}
    </Popover>
  )

export const ProfileSlot = () => {
  const {
    isPackagingSupplier,
    sparkUser,
    isInternal,
    canImpersonate,
    setSparkUser,
  } = useContext(UserContext)
  const { logout, session } = useAuth()
  const { canvasScreenSize } = useResponsiveLayout()
  const navigate = useNavigate()
  const makeToast = useToastAlert()
  const [isImpersonationOpen, setIsImpersonationOpen] = useState(false)
  const isImpersonating = localStorage.getItem(IS_IMPERSONATING_KEY) === 'true'
  const showImpersonationAction = canShowImpersonationAction({
    isInternal,
    canImpersonate,
    isImpersonating,
  })

  const isLargeScreen = canvasScreenSize === 'lg'
  const displayNameFromSession = (
    session?.userInfo?.displayname
      ? session.userInfo.displayname.replace(/\./g, ' ')
      : ''
  ).trim()
  const displayNameFromSparkUser = `${sparkUser?.firstName || ''}${sparkUser?.lastName ? ` ${sparkUser.lastName}` : ''}`
  // Prefer the display name from the session if available, otherwise fall back to the Spark user name. Don't use ?? in case the session display name is an empty string, which is falsy but not nullish.
  const displayName = isImpersonating
    ? displayNameFromSparkUser || displayNameFromSession
    : displayNameFromSession || displayNameFromSparkUser
  const companyName = sparkUser?.companyName ?? session?.userInfo?.company ?? ''
  const firstName = isImpersonating
    ? (sparkUser?.firstName ?? '')
    : (session?.userInfo?.firstName ?? sparkUser?.firstName ?? '')
  const lastName = isImpersonating
    ? (sparkUser?.lastName ?? '')
    : (session?.userInfo?.lastName ?? sparkUser?.lastName ?? '')
  const lanId = isImpersonating
    ? (sparkUser?.lanId ?? '')
    : (session?.userInfo?.lanId ?? sparkUser?.lanId ?? '')

  const handleRevertToOriginalUser = async () => {
    if (!session?.accessToken) return
    try {
      const originalUser = await restoreOriginalUser({
        accessToken: session.accessToken,
        email: session.userInfo?.email ?? '',
        authDomain: 'ENTERPRISE',
      })
      applyOriginalUser(originalUser, setSparkUser)
    } catch (error) {
      console.error('Unable to restore original user', error)
      makeToast({
        variant: 'error',
        heading: 'Unable to revert impersonation',
        message: 'Please try again or contact support.',
      })
    }
  }

  return (
    <Grid.Container>
      <Separator direction="vertical" />
      <Grid.Item className="hc8-pl-2x">
        <ProfileWrapper
          displayName={displayName}
          isLargeScreen={isLargeScreen}
          companyName={companyName}
        >
          <Menu location="bottom-right">
            <Menu.Toggle
              variant="ghost"
              size="lg"
              aria-label="Profile menu"
              isIconOnly={!isLargeScreen}
            >
              {!isLargeScreen ? (
                <div>
                  <UserAvatar
                    firstName={firstName}
                    lastName={lastName}
                    lanId={lanId}
                  />
                </div>
              ) : (
                <Grid.Container align="center">
                  <Grid.Item>
                    <UserAvatar
                      firstName={firstName}
                      lastName={lastName}
                      lanId={lanId}
                    />
                  </Grid.Item>
                  <Grid.Item>
                    <Grid.Container
                      direction="column"
                      spacing="none"
                      align="start"
                    >
                      <Grid.Item className="hc8-clr-base hc8-fs-100 profile-slot-user-text-overflow">
                        <strong>{displayName}</strong>
                      </Grid.Item>
                      <Grid.Item className="hc8-clr-base hc8-fs-100 profile-slot-user-text-overflow">
                        {companyName}
                      </Grid.Item>
                    </Grid.Container>
                  </Grid.Item>
                </Grid.Container>
              )}
            </Menu.Toggle>
            {!isPackagingSupplier && (
              <Menu.Item onClick={() => navigate('/account')}>
                Account
              </Menu.Item>
            )}
            {showImpersonationAction && !isPol && (
              <Menu.Item onClick={() => setIsImpersonationOpen(true)}>
                Impersonation
              </Menu.Item>
            )}
            {isImpersonating && !isPol && (
              <Menu.Item onClick={handleRevertToOriginalUser}>
                Revert to original user
              </Menu.Item>
            )}
            <Menu.Item onClick={logout}>Log out</Menu.Item>
          </Menu>
        </ProfileWrapper>
      </Grid.Item>
      <ImpersonationModal
        isOpen={isImpersonationOpen}
        onClose={() => setIsImpersonationOpen(false)}
      />
    </Grid.Container>
  )
}
